import { Row } from '@cuemath/comps';
import { Text } from '@cuemath/web-comps';
import React from 'react';
import styled from 'styled-components';

import COLORS from '../../../constants/colors';
import {
  ALL_HALF_HOURS,
  BLOCK_STATUSES,
  epochDateKeyInZone,
  formatDateColumnInZone,
  formatEpochTimeInZone,
} from '../common/constants';

// Six steps of green, so a busy half-hour reads darker than a thin one.
const DENSITY = [
  'rgba(0, 158, 96, 0.10)',
  'rgba(0, 158, 96, 0.22)',
  'rgba(0, 158, 96, 0.36)',
  'rgba(0, 158, 96, 0.52)',
  'rgba(0, 158, 96, 0.70)',
  'rgba(0, 158, 96, 0.88)',
];
const DENSITY_STEPS = DENSITY.length;

const Wrapper = styled.div`
  margin-top: 16px;
  width: 100%;
  overflow: auto;
  max-height: 70vh;
  border: 1px solid ${COLORS.GRAY_LIGHT};
  border-radius: 5px;
  background: ${COLORS.WHITE};
`;
const Grid = styled.table`
  border-collapse: separate;
  border-spacing: 0;
  width: 100%;
  font-size: 13px;

  thead th {
    position: sticky;
    top: 0;
    z-index: 3;
    background: ${COLORS.WILD_SAND};
    border-bottom: 1px solid ${COLORS.GRAY_LIGHT};
    padding: 8px 10px;
    text-align: center;
    font-weight: 600;
    white-space: nowrap;
  }

  thead th.corner {
    left: 0;
    z-index: 5;
    text-align: left;
  }
`;
const TimeCell = styled.th`
  position: sticky;
  left: 0;
  z-index: 2;
  background: ${COLORS.WILD_SAND};
  width: 82px;
  min-width: 82px;
  padding: 0 10px;
  text-align: right;
  font-size: 11.5px;
  font-weight: 600;
  color: ${COLORS.GRAY};
  border-right: 1px solid ${COLORS.GRAY_LIGHT};
  border-bottom: 1px solid ${COLORS.WILD_SAND};
  font-variant-numeric: tabular-nums;
`;
const Cell = styled.td`
  height: 32px;
  padding: 0;
  text-align: center;
  font-size: 12.5px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  border-right: 1px solid ${COLORS.WILD_SAND};
  border-bottom: 1px solid ${COLORS.WILD_SAND};
  color: ${({ color }) => color || COLORS.GRAY};
  background: ${({ background }) => background || 'transparent'};
  cursor: ${({ interactive }) => (interactive ? 'default' : 'auto')};

  &:focus-visible {
    outline: 2px solid ${COLORS.BOSTON_BLUE};
    outline-offset: -2px;
  }
`;
const Tip = styled.div`
  position: fixed;
  z-index: 60;
  pointer-events: none;
  max-width: 420px;
  padding: 11px 13px;
  border-radius: 5px;
  border: 1px solid ${COLORS.GRAY_LIGHT};
  background: ${COLORS.WHITE};
  box-shadow: 0 10px 28px -18px rgba(14, 21, 25, 0.5);
  font-size: 12.5px;
`;
const TipGroup = styled.div`
  margin-top: 8px;
  padding-top: 6px;
  border-top: 1px solid ${COLORS.WILD_SAND};
  font-size: 10.5px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ color }) => color};

  &:first-of-type {
    margin-top: 0;
    padding-top: 0;
    border-top: 0;
  }
`;
const TipLine = styled.div`
  color: ${COLORS.BLACK};
  line-height: 1.35;
  text-transform: none;
  letter-spacing: 0;
  font-size: 12.5px;
`;
const Toggle = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: ${COLORS.GRAY};
  cursor: pointer;
`;
const Scale = styled(Row)`
  align-items: center;
  gap: 0;
  font-size: 12px;
  color: ${COLORS.GRAY};
`;
const Step = styled.span`
  width: 22px;
  height: 12px;
  display: block;
  background: ${({ background }) => background};
`;

/**
 * Bucket every block by "date|time" in the viewer's zone.
 *
 * Both halves of the key are recomputed per zone, not just relabelled: 01:00 IST is
 * the previous day in the US, so a block moves column as well as row.
 */
const buildCells = (specialists, zone) => {
  const cells = new Map();

  (specialists || []).forEach(specialist =>
    (specialist.blocks || []).forEach(block => {
      const date = epochDateKeyInZone(block.start, zone) || block.date;
      const time = formatEpochTimeInZone(block.start, zone);

      if (!date || time === '-') return;

      const key = `${date}|${time}`;

      if (!cells.has(key)) {
        cells.set(key, { available: [], occupied: [], paused: [] });
      }

      const bucket = cells.get(key)[block.status];

      if (bucket) bucket.push({ ...block, specialist });
    }),
  );

  return cells;
};

// The printed number is always the free count; occupied/paused live in the tooltip.
const cellStyle = cell => {
  if (!cell) return {};

  const free = cell.available.length;

  if (free) {
    return {
      background: DENSITY[Math.min(free, DENSITY_STEPS) - 1],
      color: free > 3 ? COLORS.WHITE : COLORS.BLACK,
      count: free,
    };
  }

  // Nobody free, but the slot is not empty — tint it and say 0, so it reads as
  // checked rather than absent.
  const status = cell.occupied.length ? BLOCK_STATUSES.occupied : BLOCK_STATUSES.paused;

  return cell.occupied.length || cell.paused.length
    ? { background: status.background, color: status.color, count: 0 }
    : {};
};

const TIP_GROUPS = [
  { key: 'available', status: BLOCK_STATUSES.available },
  { key: 'occupied', status: BLOCK_STATUSES.occupied },
  { key: 'paused', status: BLOCK_STATUSES.paused },
];

const Calendar = ({ specialists, dates, zone, zoneLabel }) => {
  const [showAllHours, setShowAllHours] = React.useState(false);
  const [tip, setTip] = React.useState(null);

  const cells = React.useMemo(() => buildCells(specialists, zone), [specialists, zone]);

  const times = React.useMemo(() => {
    if (showAllHours) return ALL_HALF_HOURS;

    const present = new Set();

    cells.forEach((_, key) => present.add(key.split('|')[1]));

    return [...present].sort();
  }, [cells, showAllHours]);

  const showTip = React.useCallback((event, key, cell) => {
    if (!cell) return;

    const rect = event.currentTarget.getBoundingClientRect();
    const [date, time] = key.split('|');

    setTip({ cell, date, time, x: rect.right + 10, y: rect.top });
  }, []);

  const hideTip = React.useCallback(() => setTip(null), []);

  if (!dates.length || !times.length) {
    return (
      <Wrapper>
        <Row justifyContent="center" style={{ padding: '32px' }}>
          <Text variant="body2" color={COLORS.GRAY}>
            No offered slots in this window.
          </Text>
        </Row>
      </Wrapper>
    );
  }

  return (
    <>
      <Row
        justifyContent="space-between"
        alignItems="center"
        style={{ marginTop: '12px' }}
      >
        <Toggle>
          <input
            type="checkbox"
            checked={showAllHours}
            onChange={event => setShowAllHours(event.target.checked)}
          />
          Show empty hours
        </Toggle>
        <Scale gutter={0}>
          <Text variant="body3" color={COLORS.GRAY}>
            Specialists free&nbsp;
          </Text>
          {DENSITY.map(background => (
            <Step key={background} background={background} />
          ))}
          <Text variant="body3" color={COLORS.GRAY}>
            &nbsp;1 → {DENSITY_STEPS}+
          </Text>
        </Scale>
      </Row>

      <Wrapper>
        <Grid>
          <thead>
            <tr>
              <th className="corner">{zoneLabel}</th>
              {dates.map(date => (
                <th key={date}>{formatDateColumnInZone(date, zone)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {times.map(time => (
              <tr key={time}>
                <TimeCell>{time}</TimeCell>
                {dates.map(date => {
                  const key = `${date}|${time}`;
                  const cell = cells.get(key);
                  const { background, color, count } = cellStyle(cell);

                  return (
                    <Cell
                      key={key}
                      background={background}
                      color={color}
                      interactive={Boolean(cell)}
                      tabIndex={cell ? 0 : -1}
                      onMouseEnter={event => showTip(event, key, cell)}
                      onMouseLeave={hideTip}
                      onFocus={event => showTip(event, key, cell)}
                      onBlur={hideTip}
                    >
                      {count === undefined ? '' : count}
                    </Cell>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </Grid>
      </Wrapper>

      {tip && (
        <Tip style={{ left: `${tip.x}px`, top: `${tip.y}px` }} role="tooltip">
          <Text variant="body2">
            {formatDateColumnInZone(tip.date, zone)}, {tip.time}
          </Text>
          {TIP_GROUPS.map(({ key, status }) => {
            const rows = tip.cell[key];

            if (!rows.length) return null;

            return (
              <TipGroup key={key} color={status.color}>
                {status.label} ({rows.length})
                {rows.map(row => (
                  <TipLine key={`${row.specialist.teacher_id}-${row.start}`}>
                    {row.specialist.teacher_name || row.specialist.teacher_id}
                    {row.student_name ? ` · ${row.student_name}` : ''}
                  </TipLine>
                ))}
              </TipGroup>
            );
          })}
        </Tip>
      )}
    </>
  );
};

export default Calendar;
